import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import * as _ from 'lodash';
import type { FunctionComponent, VoidFunctionComponent } from 'react';
import ModalPlans from '../components/settings/subscription/plans/ModalPlans';
import { PlansProvider } from '../components/settings/subscription/plans/PlansContext';
import type { MessageOptions } from '../components/settings/billing/features/consts';

//  TODO: when we update the modal to the new props, simply remove this and use the new Props type directly.
type PlanSelectModalProps = MessageOptions & {
  /**
   * We allow the user to pass a custom onHide function, which will be called when the modal is closed.
   * @deprecated This is deprecated, and will be removed when we update the modal to the new props.
   */
  onHide?: () => void;
};

/**
 * This hook just manages if the modal is open or closed.
 */
function useIsModalShowing() {
  const [isShowing, setIsShowing] = useState<boolean>(false);
  return {
    isShowing,
    onOpen: () => setIsShowing(true),
    onHide: () => setIsShowing(false),
  };
}

/**
 * Hook for managing the props for the modal
 */
function useModalProps() {
  const [props, setProps] = useState<PlanSelectModalProps | null>(null);
  return {
    props,
    setProps,
  };
}

//  The only things we need to expose here are functions for opening the modal with specific props,
//  and a function for closing the modal (which consumers probably don't even need -- remove if this is the case).
type PlanSelectorContextType = {
  showPlanSelectorModal: (messageOptions?: MessageOptions) => void;
  onHide: () => void;
};

const PlanSelectorContext = createContext<PlanSelectorContextType>({
  //  Note that these are just stubs, but it doesn't matter because the context provider/wrapper is rendered which actually sets the values.
  showPlanSelectorModal: () => {},
  onHide: () => {},
});

/**
 * Hook to use the PlanSelectorContext.
 *
 * @example
 * const { showPlanSelectorModal } = usePlanSelectorContext();
 *
 * return (
 *   <button onClick={() => showPlanSelectorModal({ focusFeature: 'compliance' })}>
 * )
 */
export const usePlanSelectorContext = () => useContext(PlanSelectorContext);

type ModalPlanSelectorWrapperProps = {
  isShowing: boolean;
  messageOptions: MessageOptions;
  onHide: () => void;
};

const ModalPlanSelectorWrapper: VoidFunctionComponent<ModalPlanSelectorWrapperProps> = ({
  isShowing,
  onHide,
  messageOptions,
}: ModalPlanSelectorWrapperProps) => {
  return (
    <PlansProvider>
      <ModalPlans onHide={onHide} isShowing={isShowing} messageOptions={messageOptions} />
    </PlansProvider>
  );
};

/**
 * Provider component which will wrap the entire app, making the showPlanSelectorModal
 * function available to all components below it.
 * Dont' use this directly, use the usePlanSelectorContext hook instead.
 */
const PlanSelectorContextProvider: FunctionComponent = ({ children }) => {
  const { isShowing, onOpen, onHide } = useIsModalShowing();
  const { props, setProps } = useModalProps();
  //  Function which is called when we want to show the modal.
  //  It sets the props and opens the modal.
  const showPlanSelectorModal = useCallback((messageOptions?: MessageOptions) => {
    setProps(messageOptions || null);
    onOpen();
  }, [onOpen, setProps]);

  //  It is always a good practice to memoize the value of the context provider,
  //  so that children using the context hook don't re-render unnecessarily.
  const contextValue = useMemo(() => ({
    showPlanSelectorModal,
    onHide,
  }), [showPlanSelectorModal, onHide]);

  const customOnHide = props?.onHide || _.noop;
  const handleOnHide = () => {
    //  Unset the props
    setProps(null);
    //  Call the custom onHide function, if it exists.
    customOnHide();
    //  Call the real onHide function.
    onHide();
  };

  return (
    <PlanSelectorContext.Provider value={contextValue}>
      {children}
      <ModalPlanSelectorWrapper
        isShowing={isShowing}
        onHide={handleOnHide}
        messageOptions={_.omit(props, 'onHide')}
      />
    </PlanSelectorContext.Provider>
  );
};

export default PlanSelectorContextProvider;
